import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useSubscription = () => {
  const [userSubscription, setUserSubscription] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [trialUsers, setTrialUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchUserSubscription();
    }
    fetchSubscriptionStats();
  }, [user]);

  const fetchUserSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions_mt2024')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        // Create trial subscription for new users
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 20);

        const { data: newSubscription, error: createError } = await supabase
          .from('user_subscriptions_mt2024')
          .insert([{
            user_id: user.id,
            status: 'trial',
            trial_end_date: trialEndDate.toISOString(),
            created_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (createError) throw createError;
        setUserSubscription({ ...newSubscription, days_left: 20 });
      } else {
        // Calculate days left for trial users
        if (data.status === 'trial' && data.trial_end_date) {
          const daysLeft = Math.max(0, Math.ceil(
            (new Date(data.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)
          ));
          setUserSubscription({ ...data, days_left: daysLeft });

          // Auto-expire trial if needed
          if (daysLeft <= 0 && data.status === 'trial') {
            await updateSubscriptionStatus(user.id, 'expired');
          }
        } else {
          setUserSubscription(data);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStats = async () => {
    try {
      // Fetch subscription statistics
      const { data: stats, error } = await supabase
        .rpc('get_subscription_stats_mt2024');

      if (error) throw error;
      setSubscriptionStats(stats);

      // Fetch payment history
      const { data: payments, error: paymentsError } = await supabase
        .from('payments_mt2024')
        .select(`
          *,
          users_mt2024 (
            name,
            email
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (paymentsError) throw paymentsError;
      setPaymentHistory(payments || []);

      // Fetch trial users
      const { data: trials, error: trialsError } = await supabase
        .from('user_subscriptions_mt2024')
        .select(`
          *,
          users_mt2024 (
            name,
            email
          )
        `)
        .eq('status', 'trial')
        .order('created_at', { ascending: false });

      if (trialsError) throw trialsError;
      
      const trialsWithDaysLeft = trials?.map(trial => ({
        ...trial,
        days_left: Math.max(0, Math.ceil(
          (new Date(trial.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)
        ))
      })) || [];
      
      setTrialUsers(trialsWithDaysLeft);
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
    }
  };

  const processPayment = async (paymentData) => {
    try {
      setLoading(true);

      // Create payment record
      const { data: payment, error: paymentError } = await supabase
        .from('payments_mt2024')
        .insert([{
          user_id: paymentData.user_id,
          amount: paymentData.amount,
          currency: paymentData.currency,
          status: 'completed', // In real app, this would be 'pending' until confirmed
          transaction_id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          payment_method: 'credit_card',
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Update user subscription to active
      const { error: subscriptionError } = await supabase
        .from('user_subscriptions_mt2024')
        .update({
          status: 'active',
          upgraded_at: new Date().toISOString(),
          trial_end_date: null
        })
        .eq('user_id', paymentData.user_id);

      if (subscriptionError) throw subscriptionError;

      // Refresh user subscription
      await fetchUserSubscription();

      return { success: true, payment };
    } catch (error) {
      console.error('Error processing payment:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateSubscriptionStatus = async (userId, status) => {
    try {
      const { error } = await supabase
        .from('user_subscriptions_mt2024')
        .update({ status })
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating subscription status:', error);
    }
  };

  const extendTrial = async (userId, days) => {
    try {
      const { data: subscription } = await supabase
        .from('user_subscriptions_mt2024')
        .select('trial_end_date')
        .eq('user_id', userId)
        .single();

      if (subscription) {
        const currentEndDate = new Date(subscription.trial_end_date);
        const newEndDate = new Date(currentEndDate);
        newEndDate.setDate(newEndDate.getDate() + days);

        const { error } = await supabase
          .from('user_subscriptions_mt2024')
          .update({ trial_end_date: newEndDate.toISOString() })
          .eq('user_id', userId);

        if (error) throw error;
        
        toast.success(`Trial extended by ${days} days`);
        await fetchSubscriptionStats();
      }
    } catch (error) {
      console.error('Error extending trial:', error);
      toast.error('Failed to extend trial');
    }
  };

  const refundPayment = async (paymentId) => {
    try {
      const { error } = await supabase
        .from('payments_mt2024')
        .update({ status: 'refunded' })
        .eq('id', paymentId);

      if (error) throw error;

      toast.success('Refund processed successfully');
      await fetchSubscriptionStats();
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error('Failed to process refund');
    }
  };

  const dismissTrialBanner = async () => {
    try {
      const { error } = await supabase
        .from('user_subscriptions_mt2024')
        .update({ dismissed: true })
        .eq('user_id', user.id);

      if (error) throw error;
      
      setUserSubscription(prev => ({ ...prev, dismissed: true }));
    } catch (error) {
      console.error('Error dismissing banner:', error);
    }
  };

  return {
    userSubscription,
    subscriptionStats,
    paymentHistory,
    trialUsers,
    loading,
    processPayment,
    extendTrial,
    refundPayment,
    dismissTrialBanner,
    fetchUserSubscription
  };
};